import { useState } from 'react';
import { AlertTriangle, Clock, CornerDownRight, Tag } from 'lucide-react';
import type { QuizAnswer, QuizQuestion, VideoAnswerNext, VideoSegment } from '../../types/quizzes';
import { formatTime } from '../../lib/videoQuiz';
import { TagChipPicker, TimeInput, type CfTag } from './controls';

/** Per-question video fields: follow-up toggle, stop time + ClickFunnels attribute. */
export function VideoQuestionFields({
  question, onChange, onToggleFollowUp, unmapped, neverAsked = false,
}: {
  question: QuizQuestion;
  onChange: (patch: Partial<QuizQuestion>) => void;
  onToggleFollowUp: (on: boolean) => void;
  unmapped: boolean;
  /** Follow-ups only: no answer asks this question. */
  neverAsked?: boolean;
}) {
  const followUp = question.followUp === true;
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <label className="text-xs text-ink-3 mb-1 flex items-center gap-1.5 cursor-pointer" title="A follow-up has no stop time. It's shown only when a viewer picks an answer set to ask it.">
          <input type="checkbox" checked={followUp} onChange={(e) => onToggleFollowUp(e.target.checked)} className="rounded" />
          Follow-up question
        </label>
        {followUp ? (
          <div className="h-[38px] flex items-center gap-1 text-xs text-ink-3"><CornerDownRight className="w-3.5 h-3.5" /> Shown when an answer asks it</div>
        ) : (
          <div className="flex items-center gap-1.5">
            <Clock className="w-3 h-3 text-ink-3" />
            <span className="text-xs text-ink-3">Stops at</span>
            <TimeInput value={question.at} onChange={(v) => onChange({ at: v })} ariaLabel="Question stop time" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-[200px]">
        <label className="text-xs text-ink-3 mb-1 block">Save answer to ClickFunnels field</label>
        <input
          type="text"
          value={question.attributeKey ?? ''}
          onChange={(e) => onChange({ attributeKey: e.target.value || undefined })}
          placeholder="custom attribute key, e.g. biggest_challenge"
          className="ds-input w-full font-mono text-xs"
        />
      </div>
      {unmapped && (
        <span className="flex items-center gap-1 text-xs text-warning pb-2" title="Answers are deleted from MyLeadSyncer after they sync to ClickFunnels">
          <AlertTriangle className="w-3.5 h-3.5" /> Not mapped
        </span>
      )}
      {followUp && neverAsked && (
        <span className="flex items-center gap-1 text-xs text-warning pb-2" title="Pick this question in another answer's &quot;Then&quot; menu, or viewers will never see it">
          <AlertTriangle className="w-3.5 h-3.5" /> Never asked
        </span>
      )}
    </div>
  );
}

function nextToValue(next: VideoAnswerNext | undefined): string {
  if (next?.type === 'clip') return `clip:${next.segmentId}`;
  if (next?.type === 'ask') return `ask:${next.questionId}`;
  return 'continue';
}

function valueToNext(v: string): VideoAnswerNext {
  if (v.startsWith('clip:')) return { type: 'clip', segmentId: v.slice(5) };
  if (v.startsWith('ask:')) return { type: 'ask', questionId: v.slice(4) };
  return { type: 'continue' };
}

/** Per-answer video fields: what plays next (continue / clip / follow-up) + tags applied when chosen. */
export function VideoAnswerFields({
  answer, segments, followUps, cfTags, onChange,
}: {
  answer: QuizAnswer;
  segments: VideoSegment[];
  /** Follow-up questions this answer may ask (the answer's own question already excluded). */
  followUps: QuizQuestion[];
  cfTags: CfTag[];
  onChange: (patch: Partial<QuizAnswer>) => void;
}) {
  const [showTags, setShowTags] = useState(false);
  const nextValue = nextToValue(answer.next);
  const tagIds = answer.tagIds ?? [];
  const tagNames = tagIds.map((id) => cfTags.find((t) => String(t.id) === id)?.name ?? id);
  const missingClip = answer.next?.type === 'clip' && !segments.some((s) => `clip:${s.id}` === nextValue);
  const missingAsk = answer.next?.type === 'ask' && !followUps.some((q) => `ask:${q.id}` === nextValue);
  const missing = missingClip || missingAsk;

  return (
    <div className="w-full pl-0 md:pl-2 pb-1">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-ink-3">Then</span>
        <select
          value={missing ? '' : nextValue}
          onChange={(e) => onChange({ next: valueToNext(e.target.value) })}
          className={`ds-input !py-1 text-xs w-auto max-w-full ${missing ? '!border-danger' : ''}`}
        >
          {missing && <option value="">{missingClip ? 'Missing clip' : 'Missing follow-up'} — choose again</option>}
          <option value="continue">Continue</option>
          {segments.length > 0 && (
            <optgroup label="Play a clip">
              {segments.map((s) => (
                <option key={s.id} value={`clip:${s.id}`}>Play clip: {s.label} ({formatTime(s.start)}–{formatTime(s.end)})</option>
              ))}
            </optgroup>
          )}
          {followUps.length > 0 && (
            <optgroup label="Ask a follow-up">
              {followUps.map((q) => (
                <option key={q.id} value={`ask:${q.id}`}>Ask follow-up → {q.title || 'Untitled question'}</option>
              ))}
            </optgroup>
          )}
        </select>
        <button
          type="button"
          onClick={() => setShowTags((v) => !v)}
          className={`flex items-center gap-1 px-2 py-1 rounded border ${tagIds.length ? 'bg-accent-soft border-accent text-accent-ink' : 'border-line text-ink-3 hover:border-ink-3'}`}
        >
          <Tag className="w-3 h-3" /> {tagIds.length ? `Tags: ${tagNames.join(', ')}` : 'Add tags'}
        </button>
      </div>
      {showTags && (
        <div className="mt-2 p-2 border border-line rounded-lg bg-surface">
          <TagChipPicker tags={cfTags} selected={tagIds} onChange={(next) => onChange({ tagIds: next.length ? next : undefined })} />
        </div>
      )}
    </div>
  );
}

/** Banner listing questions whose answers would be lost after the ClickFunnels sync. */
export function UnmappedWarning({ questions }: { questions: QuizQuestion[] }) {
  if (questions.length === 0) return null;
  return (
    <div className="mb-4 p-3 bg-warning-soft border border-line rounded-lg text-sm">
      <div className="flex items-center gap-2 font-medium text-warning">
        <AlertTriangle className="w-4 h-4" /> These questions aren't mapped to a ClickFunnels field or tags — their answers will be lost after sync
      </div>
      <p className="text-xs text-ink-2 mt-1 mb-1.5">
        Video quiz answers are deleted from MyLeadSyncer once they sync, so ClickFunnels is the only place they're kept.
        Set "Save answer to ClickFunnels field" or add tags to the answers.
      </p>
      <ul className="list-disc ml-5 text-ink-2 text-xs space-y-0.5">
        {questions.map((q) => <li key={q.id}>{q.title || 'Untitled question'}{q.followUp ? ' (follow-up)' : q.at !== undefined ? ` (${formatTime(q.at)})` : ''}</li>)}
      </ul>
    </div>
  );
}
